
import { Component, Prop, Ref, Vue } from "vue-property-decorator"
import { BFormTextarea } from "bootstrap-vue"

import { Getter } from "vuex-class"
import { TenantFeatures } from "@/types/tenantFeatures"

const RichTextEditorHugeRTE = () => import("./RichTextEditorHugeRTE.vue")
const RichTextEditor = () => import("./RichTextEditor.vue")

@Component({
	components: {
		"rich-text-MCE": RichTextEditorHugeRTE,
		"rich-text": RichTextEditor
	}
})
export default class TextAreaToggleExpand extends Vue {
	/**
	 * The maximum number of rows to show when not expanded
	 */
	@Prop({ type: Number, default: 8 })
	public unexpandedMaxRows!: number;

	/**
	 * The minimum number of rows to show
	 * Anything <2 will be set to 2
	 * @see https://bootstrap-vue.org/docs/components/form-textarea#displayed-rows
	 */
	@Prop({ type: Number, default: 2 })
	public minRows!: number;

	/**
	 * Value for v-model support
	 */
	@Prop({ type: String, default: "" })
	public value!: string;

	@Prop({ type: Boolean, default: false })
	public readonly!: boolean;

	@Prop({ default: () => null })
	public placeholder!: string | null

	@Prop({ type: Boolean, default: false })
	public allowRichText!: boolean

	@Prop({ default: null })
	public minHeight!: string | null

	@Prop({ type: String, default: "300px" })
	public contractedMaxHeight!: string

	@Prop({ type: Boolean, default: false })
	public expandedByDefault!: boolean

	@Prop({ type: Boolean, default: false })
	public userHistoryOnly!: boolean

	@Prop({ type: Boolean, default: true })
	public allowImages!: boolean

	@Ref()
	textArea!: BFormTextarea

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	private isExpanded = false;
	private readonly rowHeight = 19.5

	private set text (text: string) {
		this.$emit("input", text)
	}

	private mounted () {
		if (this.expandedByDefault && !this.isExpanded) {
			this.toggleExpanded()
		}
	}

	private get text () {
		return this.value
	}

	private get maxRows () {
		if (this.isExpanded) {
			const docHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
			return Math.floor(docHeight / this.rowHeight) - 8 // 8 rows accounts for top/bottom bars in case creation
		}
		return this.unexpandedMaxRows
	}

	private get iconClass () {
		if (this.isExpanded) {
			return "fas fa-compress-alt"
		}
		return "fas fa-expand-alt"
	}

	private get richTextStyle (): any {
		return this.isExpanded
			? {}
			: {
				"max-height": this.contractedMaxHeight,
				"min-height": this.minHeight
			}
	}

	public get useNewRichText (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.NewRichText)
	}

	public get richTextControl () {
		return this.useNewRichText
			? () => import("./RichTextEditorHugeRTE.vue")
			: () => import("./RichTextEditor.vue")
	}

	private toggleExpanded () {
		this.isExpanded = !this.isExpanded

		this.forceTextAreaHeight()
	}

	/**
	 * Forces the bootstrap text-area to re-render
	 * @private
	 */
	private forceTextAreaHeight (): void {
		// From https://github.com/bootstrap-vue/bootstrap-vue/blob/dev/src/components/form-textarea/form-textarea.js
		// setHeight calculates the height of the text area on the next tick
		if (this.textArea) {
			this.textArea.setHeight()
		}
	}
}
